/**
 * VideoEmbedSection Component
 *
 * Embeds YouTube or Vimeo videos in a responsive 16:9 container.
 * Supports various URL formats and preserves timestamps/hashes.
 */
import React, { useId } from "react";
import { filterClass } from "../../utils/class-utils";
import type { VideoEmbedSectionProps } from "../types";
import {
  getAccordionButtonClass,
  getAccordionCollapseBaseClass,
  getAccordionItemClass,
  getAccordionToggleButtonProps,
  isEasyblocksEditorMode,
  resolveAccordionExpanded,
  resolveAccordionStartOpen,
} from "../utils/accordion-shell";

function getEmbedOrigin(): string | undefined {
  if (typeof window === "undefined") return undefined;

  const { origin, protocol } = window.location;
  if (protocol !== "http:" && protocol !== "https:") return undefined;

  return origin;
}

/**
 * Parse YouTube or Vimeo URL and return embed URL with enhanced parameters
 */
function parseVideoUrl(
  url: string,
  embedOrigin?: string
): { embedUrl: string; provider: 'youtube' | 'vimeo' | null } {
  if (!url) return { embedUrl: '', provider: null };
  
  try {
    const urlObj = new URL(url.trim());
    
    // YouTube detection
    if (urlObj.hostname.includes('youtube.com') || urlObj.hostname.includes('youtu.be')) {
      let videoId = '';
      
      if (urlObj.hostname.includes('youtu.be')) {
        // youtu.be/VIDEO_ID or youtu.be/VIDEO_ID?t=123
        videoId = urlObj.pathname.slice(1).split('/')[0];
      } else if (urlObj.pathname.includes('/embed/')) {
        // youtube.com/embed/VIDEO_ID
        videoId = urlObj.pathname.split('/embed/')[1]?.split('/')[0];
      } else {
        // youtube.com/watch?v=VIDEO_ID or youtube.com/watch?v=VIDEO_ID&t=123
        videoId = urlObj.searchParams.get('v') || '';
      }
      
      if (videoId && /^[a-zA-Z0-9_-]{11}$/.test(videoId)) {
        // Preserve timestamp parameter if present
        const timestamp = urlObj.searchParams.get('t') || urlObj.searchParams.get('start');
        const params = new URLSearchParams({
          ...(timestamp ? { start: timestamp } : {}),
          ...(embedOrigin ? { origin: embedOrigin } : {}),
          rel: '0',           // Don't show related videos from other channels
          modestbranding: '1' // Minimal YouTube branding
        });
        const embedUrl = `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
        return { embedUrl, provider: 'youtube' };
      }
    }
    
    // Vimeo detection
    if (urlObj.hostname.includes('vimeo.com')) {
      let videoId = '';
      
      if (urlObj.pathname.includes('/video/')) {
        // player.vimeo.com/video/VIDEO_ID
        videoId = urlObj.pathname.split('/video/')[1]?.split('/')[0];
      } else {
        // vimeo.com/VIDEO_ID or vimeo.com/VIDEO_ID?h=hash
        videoId = urlObj.pathname.slice(1).split('/')[0];
      }
      
      if (videoId && /^\d+$/.test(videoId)) {
        // Preserve hash parameter if present (for private videos)
        const hash = urlObj.searchParams.get('h');
        const params = new URLSearchParams({
          ...(hash ? { h: hash } : {}),
          dnt: '1',           // Do not track
          title: '0',         // Hide title
          byline: '0',        // Hide author
          portrait: '0'       // Hide author portrait
        });
        const embedUrl = `https://player.vimeo.com/video/${videoId}?${params.toString()}`;
        return { embedUrl, provider: 'vimeo' };
      }
    }
    
  } catch (e) {
    // Invalid URL, fall through to return null
  }
  
  return { embedUrl: '', provider: null };
}

export function VideoEmbedSection(props: VideoEmbedSectionProps) {
  const {
    videoUrl = "",
    paddingYClass = "py-section-md",
    bgClass = "no-class",
    videoWidthClass = "container-max-xl",
    viewMode = "video",
    accordionStartOpen = "false",
  } = props;

  const { embedUrl, provider } = parseVideoUrl(videoUrl, getEmbedOrigin());
  const hasValidVideo = Boolean(embedUrl && provider);
  const reactId = useId();
  const accordionId = `video-embed-accordion-${reactId.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const isStartOpen = resolveAccordionStartOpen(accordionStartOpen);
  const isInEditor = isEasyblocksEditorMode();
  const isAccordionExpanded = resolveAccordionExpanded(isInEditor, isStartOpen);

  const sectionClassName = ["section section-video-embed", filterClass(bgClass), filterClass(paddingYClass)]
    .filter(Boolean)
    .join(" ")
    .trim();

  const classes = {
    container: "container-xl",
    videoWidth: filterClass(videoWidthClass) || undefined,
    videoRatio: "ratio ratio-16x9",
    editorFrame: "ratio ratio-16x9 bg-light border d-flex align-items-center justify-content-center",
    accordion: "accordion",
    accordionItem: getAccordionItemClass(),
    accordionHeader: "accordion-header",
    accordionButton: (expanded: boolean) => getAccordionButtonClass(isInEditor, expanded),
    accordionCollapse: getAccordionCollapseBaseClass(isInEditor),
    accordionBody: "accordion-body",
  };

  const renderEditorVideo = () => (
    <div className={classes.editorFrame}>
      <div className="alert alert-info mb-0" role="status">
        <div className="text-center">
          {hasValidVideo ? (
            <>
              <div className="mb-2">
                <i className="bi bi-play-circle" style={{ fontSize: "3rem" }}></i>
              </div>
              <div className="fw-semibold">{provider === "youtube" ? "YouTube" : "Vimeo"} Video</div>
              <div className="small">Video will render here in production</div>
            </>
          ) : (
            <>
              <div className="mb-2">
                <i className="bi bi-film" style={{ fontSize: "3rem" }}></i>
              </div>
              <div className="fw-semibold">No video URL</div>
              <div className="small">Enter a YouTube or Vimeo URL to embed a video</div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderVideoEmbed = () => (
    <div className={classes.videoRatio}>
      <iframe
        src={embedUrl}
        title={`${provider === "youtube" ? "YouTube" : "Vimeo"} video player`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
        style={{ border: 0 }}
      />
    </div>
  );

  const renderVideoContent = () => <div className={classes.videoWidth}>{isInEditor ? renderEditorVideo() : renderVideoEmbed()}</div>;

  // In editor mode, show placeholder
  // In production, don't render if no valid video
  if (!isInEditor && !hasValidVideo) {
    return null;
  }

  return (
    <section className={sectionClassName || undefined}>
      <div className={classes.container}>
        {viewMode === "accordion" ? (
          <div className={classes.accordion} id={accordionId}>
            <div className={classes.accordionItem}>
              <h2 className={classes.accordionHeader} id={`${accordionId}-heading`}>
                <button
                  className={classes.accordionButton(isAccordionExpanded)}
                  type="button"
                  aria-expanded={isAccordionExpanded ? "true" : "false"}
                  aria-controls={`${accordionId}-collapse`}
                  {...getAccordionToggleButtonProps(isInEditor, `${accordionId}-collapse`)}
                >
                  <span className="h4 mb-0">Video</span>
                </button>
              </h2>
              <div
                id={`${accordionId}-collapse`}
                className={[classes.accordionCollapse, isInEditor ? "show" : isStartOpen ? "show" : ""]
                  .filter(Boolean)
                  .join(" ")}
                aria-labelledby={`${accordionId}-heading`}
              >
                <div className={classes.accordionBody}>{renderVideoContent()}</div>
              </div>
            </div>
          </div>
        ) : (
          renderVideoContent()
        )}
      </div>
    </section>
  );
}

export default VideoEmbedSection;

